// ContactUs.js
import React, { useState } from 'react';
import api from '../api';
import './ContactUs.css';

function ContactUs() {
  const [formData, setFormData] = useState({
    inquiryType: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Limit message to 1400 characters
    if (name === 'message' && value.length > 1400) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSubmitStatus(null);
  };

  const validateForm = () => {
    if (!formData.inquiryType) {
      setError('Please select an inquiry type');
      return false;
    }
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.message.trim()) {
      setError('Please enter a message');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/api/contact/', formData);
      setSubmitStatus('success');
      // Clear form on success
      setFormData({
        inquiryType: '',
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <h1>Contact Us</h1>
        <p className="contact-intro">
          Have a question or need assistance? We'd love to hear from you! 
          Fill out the form below and we'll get back to you as soon as possible.
        </p>

        {submitStatus === 'success' && (
          <div className="success-message">
            <span className="success-icon">✓</span>
            Thank you for your message! We'll respond to your inquiry soon.
          </div>
        )}

        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="inquiryType">Inquiry Type *</label>
            <select
              id="inquiryType"
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleChange}
              className={!formData.inquiryType && error ? 'input-error' : ''}
            >
              <option value="">-- Select an option --</option>
              <option value="help">Help Request</option>
              <option value="inquiry">General Inquiry</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className={!formData.name.trim() && error ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className={!formData.email.trim() && error ? 'input-error' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">
              Message * 
              <span className="char-count">
                {formData.message.length}/1400
              </span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="How can we help you?"
              rows="6"
              maxLength="1400"
              className={!formData.message.trim() && error ? 'input-error' : ''}
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Email'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContactUs;
